import { BORDER_RADIUS } from "@/constants/layout";
import { DisplayMode } from "@/types";
import { Tabs, TabsProps, Tab, TabProps, alpha } from "@mui/material";

/**
 * Styled Tabs component matching the StyledTabbedShowLayout appearance.
 * Can be used anywhere in the app, not just in React Admin contexts.
 *
 * @example
 * ```tsx
 * <StyledTabs value={activeTab} onChange={handleChange}>
 *   <StyledTab label="Overview" />
 *   <StyledTab label="Details" />
 *   <StyledTab label="Settings" />
 * </StyledTabs>
 * ```
 */
export const StyledTabs = ({ sx, ...props }: TabsProps) => {
  return (
    <Tabs
      {...props}
      sx={[
        (theme) => ({
          "& .MuiTabs-flexContainer": {
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === DisplayMode.DARK ? 0.2 : 0.1),
            borderRadius: BORDER_RADIUS.XL,
            width: "fit-content",
            padding: "4px",
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

/**
 * Styled Tab component matching the StyledTabbedShowLayout appearance.
 * Should be used as children of StyledTabs.
 */
export const StyledTab = (props: TabProps) => {
  return (
    <Tab
      {...props}
      sx={{
        borderRadius: BORDER_RADIUS.XL,
        padding: "8px 16px",
        transition: "all 0.2s",
        color: "text.primary",
        minHeight: "36px",
        textTransform: "none",
        "&.Mui-selected": {
          backgroundColor: "background.paper",
          color: "text.primary",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        },
        ...(props.sx || {}),
      }}
    />
  );
};
